import type { TFunction } from 'i18next';

import { useAutoAnimate } from '@formkit/auto-animate/react';
import { cn } from 'cn';
import {
  type ChangeEvent,
  type DragEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Hint, Icon } from '~/atoms';

import { truncate } from '@vestigas/utils';
import { isFileAccepted, useFileTypesLabel } from '~/utils';

import { Alert } from './Alert/Alert';
import { Button } from './Button';

const EMPTY_ARRAY: string[] = [];

type RejectFile = NonNullable<P['rejectFile']>;

// Each file is judged against the files kept before it, so one pick that mixes
// an allowed and a vetoed file keeps the allowed one.
function addFiles(
  candidates: File[],
  selectedFiles: File[],
  isMultiSelect: boolean,
  rejectFile?: RejectFile,
): { files: File[]; rejection: string | null } {
  const kept = isMultiSelect ? [...selectedFiles] : [];
  const keptBefore = kept.length;
  let rejection: string | null = null;

  for (const file of candidates) {
    const message = rejectFile?.(file, kept) ?? null;

    if (message === null) {
      kept.push(file);
    } else {
      rejection ??= message;
    }
  }

  return { files: kept.length > keptBefore ? kept : selectedFiles, rejection };
}

// One pick can refuse files both by type and by veto; the reported message carries both.
function joinRejections(...messages: (string | null)[]) {
  return messages.filter(Boolean).join(' ') || null;
}

function resolveAcceptedFileTypesHint(
  accept: readonly string[],
  t: TFunction<'ui'>,
  override?: string,
): string {
  return (
    override ??
    t(($) => $.dataImport.acceptedFileTypes, {
      ns: 'ui',
      count: accept.length,
      types: accept
        .map((type) => type.replace('.', '').toUpperCase())
        .join(', '),
    })
  );
}

type P = {
  readonly accept?: string[];
  /** Replaces the built-in "Formats: …" line below the button. */
  readonly acceptedFileTypesLabel?: string;
  readonly buttonClassName?: string;
  readonly buttonText?: string;
  readonly disabled?: boolean;
  readonly dropzoneClassName?: string;
  /** Replaces the built-in accessible name of a selected file's remove control. */
  readonly getRemoveFileLabel?: (fileName: string) => string;
  readonly hint?: string;
  readonly isMultiSelect?: boolean;
  readonly onChange: (files: File | File[]) => void;
  readonly onReject?: (message: string) => void;
  /**
   * Vetoes a file that passed `accept`. A returned message is reported like a
   * type rejection and the file is not added; `selectedFiles` holds the files
   * kept so far, earlier files of the same pick included.
   */
  readonly rejectFile?: (
    file: File,
    selectedFiles: readonly File[],
  ) => string | null;
  readonly showAcceptedFileTypes?: boolean;
  readonly showSelectedFiles?: boolean;
} & ComponentStyling;

// fallow-ignore-next-line complexity -- shared file input keeps selection, drag state, and file-list behavior together.
export const FileInput = ({
  accept = EMPTY_ARRAY,
  acceptedFileTypesLabel,
  buttonClassName,
  buttonText,
  className,
  disabled = false,
  dropzoneClassName,
  getRemoveFileLabel,
  hint,
  isMultiSelect = false,
  onChange,
  onReject,
  rejectFile,
  showAcceptedFileTypes = true,
  showSelectedFiles = true,
  style,
}: P) => {
  const { t } = useTranslation('ui');

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);
  const fileTypesLabel = useFileTypesLabel(accept);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetFileInput = useCallback(() => {
    // Reset the file input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault();

    // `dragleave` bubbles from descendants, so crossing onto a child reports a
    // leave for the container it is still inside.
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    setIsDragging(false);
  }, []);

  const handleChange = useCallback(
    (files: File | FileList) => {
      const droppedFiles = Array.from('length' in files ? files : [files]);
      const filesArray = droppedFiles.filter((file) =>
        isFileAccepted(file, accept),
      );
      // Single-select always replaces the current file outright — including
      // with a same-named file re-picked after a previous one was rejected
      // (e.g. by the parent). Deduping by name only makes sense for multi-select,
      // where it stops the same file from being added to the list twice.
      const candidates = isMultiSelect
        ? filesArray.filter(
            (file) =>
              !selectedFiles.some(
                (selectedFile) => selectedFile.name === file.name,
              ),
          )
        : filesArray.slice(0, 1);
      const { files: newFiles, rejection: vetoMessage } = addFiles(
        candidates,
        selectedFiles,
        isMultiSelect,
        rejectFile,
      );

      const rejection = joinRejections(
        filesArray.length < droppedFiles.length
          ? t(($) => $.dataImport.invalidFileType, {
              ns: 'ui',
              types: fileTypesLabel,
            })
          : null,
        vetoMessage,
      );

      if (filesArray.length > 0) {
        // `onChange` runs a parent's own state update, so it must not happen
        // inside the `setSelectedFiles` updater above — React calls updaters
        // during its own commit, and nesting another component's setState in
        // there is what triggers "Cannot update a component while rendering a
        // different component".
        setSelectedFiles(newFiles);
        onChange(isMultiSelect ? newFiles : newFiles[0] || null);
      }

      // Reported after the accepted files: a consumer that clears its own error
      // when files arrive would otherwise wipe this message in the same drop.
      if (!rejection) {
        setRejectionMessage(null);
      } else if (onReject) {
        onReject(rejection);
      } else {
        setRejectionMessage(rejection);
      }

      // Reset input so the same file can be selected again
      resetFileInput();
    },
    [
      accept,
      fileTypesLabel,
      isMultiSelect,
      onChange,
      onReject,
      rejectFile,
      resetFileInput,
      selectedFiles,
      t,
    ],
  );

  const handleFileSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;

      if (!files?.length) {
        return;
      }

      handleChange(files);
    },
    [handleChange],
  );

  const handleFileDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      setIsDragging(false);

      const { files } = event.dataTransfer;
      if (!files?.length) {
        return;
      }

      handleChange(files);
    },
    [handleChange],
  );

  const handleFileRemove = useCallback(
    (index: number) => {
      if (disabled) {
        return;
      }

      const newFiles = selectedFiles.filter((_, index_) => index_ !== index);

      // See the comment in `handleChange` for why `onChange` runs after —
      // not inside — the `setSelectedFiles` call.
      setSelectedFiles(newFiles);
      onChange(isMultiSelect ? newFiles : newFiles[0] || null);

      // Reset the file input after removal
      resetFileInput();
    },
    [disabled, onChange, isMultiSelect, resetFileInput, selectedFiles],
  );

  const acceptedTypes = accept?.join(',') ?? '';

  const acceptedFileTypesHint = resolveAcceptedFileTypesHint(
    accept,
    t,
    acceptedFileTypesLabel,
  );

  const removeFileLabel = (fileName: string) =>
    getRemoveFileLabel?.(fileName) ??
    t(($) => $.dataImport.removeFile, { fileName });

  return (
    <div
      className={cn('flex flex-col items-center gap-4', className)}
      style={style}
    >
      <input
        ref={fileInputRef}
        accept={acceptedTypes}
        id="hidden-file-input"
        multiple={isMultiSelect}
        type="file"
        hidden
        disabled={disabled}
        onChange={handleFileSelect}
      />
      <label
        className={cn(
          'group flex h-40 w-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center text-sm transition-all duration-600 hover:border-tiga-action-lighter hover:bg-tiga-action-lighter/13',
          dropzoneClassName,
          disabled && 'cursor-not-allowed opacity-50',
          isDragging
            ? 'scale-105 border-tiga-action-lighter bg-tiga-action-lighter/13'
            : 'border-tiga-border-lighter',
        )}
        htmlFor="hidden-file-input"
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDragOver={disabled ? undefined : handleDragOver}
        onDrop={disabled ? undefined : handleFileDrop}
      >
        <Icon
          className={cn(
            'text-tiga-text-default group-hover:animate-lift group-hover:text-tiga-action-lighter',
          )}
          metaphor="uploadFile"
        />
        <div className={buttonClassName}>
          {buttonText ??
            t(($) => $.dataImport.selectFile, {
              ns: 'ui',
              count: isMultiSelect ? 2 : 1,
            })}
        </div>
        {Boolean(hint) && <Hint>{hint}</Hint>}
        {showAcceptedFileTypes && accept.length > 0 && (
          <span className="text-xs text-tiga-text-lighter">
            {acceptedFileTypesHint}
          </span>
        )}
      </label>
      {Boolean(rejectionMessage) && (
        <Alert severity="error">{rejectionMessage}</Alert>
      )}
      {showSelectedFiles && (
        <SelectedFileList
          disabled={disabled}
          files={selectedFiles}
          removeFileLabel={removeFileLabel}
          onRemove={handleFileRemove}
        />
      )}
    </div>
  );
};

const SelectedFileList = ({
  disabled,
  files,
  onRemove,
  removeFileLabel,
}: {
  readonly disabled: boolean;
  readonly files: readonly File[];
  readonly onRemove: (index: number) => void;
  readonly removeFileLabel: (fileName: string) => string;
}) => {
  const [animationParent] = useAutoAnimate();

  return (
    <ul ref={animationParent} className="w-96">
      {files.map((file, index) => (
        <li
          key={[file.name, file.lastModified, file.size].join('-')}
          className="flex h-8 items-center rounded-sm bg-tiga-surface-off-white pl-3 whitespace-nowrap transition-colors duration-200 ease-out hover:bg-tiga-action-lighter/13"
        >
          <span className="min-w-0 truncate text-sm">
            {truncate(file.name, 64, 16)}
          </span>
          <Button
            aria-label={removeFileLabel(file.name)}
            buttonStyle="link"
            className="ml-auto translate-x-1.25 transition-colors duration-300 hover:text-tiga-action-lighter"
            disabled={disabled}
            icon={<Icon metaphor="close" />}
            onClick={() => {
              onRemove(index);
            }}
          />
        </li>
      ))}
    </ul>
  );
};
